/* eslint-disable no-case-declarations */
import React from 'react'
import { client } from './../config/globals'
import deepmerge from 'deepmerge'
import { v4 as uuidv4 } from 'uuid'
import { saveAs } from 'file-saver'
import Handlebars from 'handlebars/dist/cjs/handlebars'
import {
  MergeTags,
  DisplayConditions,
  MetaData,
  Search,
  SaveMessage,
  ManageForm,
  CustomPreview,
  Mention,
  PromoAddon,
  RowAddon,
  SyncedRow,
} from './../components'
import { HTTP } from './../providers'
import products from './../config/products'

// This hack is needed temporarily due to a bug in React v3.
// We can remove this once the bug is fixed in the beta version.
const tempStore = {
  metadata: {}
}

export const getClient = () => {
  return client.default
}

export const convertHSLToHex = (h, s, l) => {
  s /= 100
  l /= 100

  let c = (1 - Math.abs(2 * l - 1)) * s,
    x = c * (1 - Math.abs((h / 60) % 2 - 1)),
    m = l - c / 2,
    r = 0,
    g = 0,
    b = 0

  if (0 <= h && h < 60) {
    r = c; g = x; b = 0
  } else if (60 <= h && h < 120) {
    r = x; g = c; b = 0
  } else if (120 <= h && h < 180) {
    r = 0; g = c; b = x
  } else if (180 <= h && h < 240) {
    r = 0; g = x; b = c
  } else if (240 <= h && h < 300) {
    r = x; g = 0; b = c
  } else if (300 <= h && h < 360) {
    r = c; g = 0; b = x
  }
  // Having obtained RGB, convert channels to hex
  r = Math.round((r + m) * 255).toString(16)
  g = Math.round((g + m) * 255).toString(16)
  b = Math.round((b + m) * 255).toString(16)

  // Prepend 0s, if necessary
  if (r.length == 1)
    r = '0' + r
  if (g.length == 1)
    g = '0' + g
  if (b.length == 1)
    b = '0' + b

  return '#' + r + g + b
}

export const getDataUrl = ({ data }) => {
  const buffer = data
  // Convert buffer to Base64 string
  let base64 = btoa(
    new Uint8Array(buffer).reduce(
      (data, byte) => data + String.fromCharCode(byte),
      ''
    )
  )
  // Create a data URL
  return `data:${'image/png'};base64,${base64}`
}

export const saveInstance = (dispatch, instance) => {
  dispatch({
    payload: {
      instance,
    }
  })
}

export const getToken = (mode) => {
  const client = getClient()
  const baseURL = client.demoAPI
  return fetch(`${baseURL}/token/${mode || 'email'}`)
    .then(res => res.json())
    .then(token => {
      return token
    })
}

export const getImageByTemplateId = (id) => {
  const client = getClient()
  const baseURL = client.demoAPI
  return `${baseURL}/getImage/${id}`
}

export const loadPage = (id) => {
  const client = getClient()
  const page = `${client.demoAPI}/getPage/${id}`
  return HTTP.get(page)
}

export const loadPopup = (id) => {
  const client = getClient()
  const popup = `${client.demoAPI}/getPopup/${id}`
  return HTTP.get(popup)
}

export const loadRow = (id) => {
  const client = getClient()
  const message = `${client.demoAPI}/getRow/${id}`
  return HTTP.get(message)
}

export const loadMessage = (id) => {
  const client = getClient()
  const message = `${client.demoAPI}/getMessage/${id}`
  return HTTP.get(message)
}

export const loadTemplate = (id) => {
  const client = getClient()
  const message = `${client.demoAPI}/templates/${id}`
  return HTTP.get(message)
}

export const onHandleUserInput = (Content, args, theme, modalAPI) => {
  const { setCloseAll, setOpen } = modalAPI
  return new Promise((resolve, reject) => {
    const close = () => {
      setCloseAll()
      reject('')
    }
    const save = (mergetags) => {
      setCloseAll()
      resolve({ ...mergetags })
    }

    const modalStyles = args?.modal?.modalStyles
      ? deepmerge(theme.modal, args.modal.modalStyles)
      : theme.modal

    const modalSettings = {
      isOpen: true,
      icon: args.modal.icon,
      hasTitleBar: true,
      title: args.modal.title,
      modalStyles,
      content: <Content close={close} save={save} args={args} />,
      close,
      save,
      key: uuidv4()
    }
    setOpen(modalSettings)
  }).catch(error => console.log(error))
}

export const contentDialogConfig = (args, theme, modalAPI, navigate) => {
  return ({
    contentDialog: {
      addOn: {
        handler: async (resolve, reject, args) => {
          let content
          if (args?.contentDialogId === 'mixed') {
            args.modal = {
              icon: 'shop',
              title: 'Add products',
              ...args
            }
            content = await onHandleUserInput(PromoAddon, args, theme, modalAPI)
          }
          if (args?.contentDialogId === 'rowAddon') {
            args.modal = {
              icon: 'shop',
              title: 'Choose products',
              ...args
            }
            content = await onHandleUserInput(RowAddon, args, theme, modalAPI)
          }

          resolve(content)
        }
      },
      saveRow: {
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'bars',
            title: 'Save Row',
            ...args
          }
          const savedRow = await onHandleUserInput(MetaData, args, theme, modalAPI)
          tempStore.metadata = savedRow
          resolve(savedRow, { synced: savedRow?.synced === 'true' })
        }
      },
      editSyncedRow: {
        label: 'Edit synced row',
        description: 'This is a synced row. You can edit it and automatically apply changes to all the designs that use the same row, or you can turn it into a standard row, and edits will only affect this design.',
        notPermittedDescription: 'This is a synced row. You can edit it and automatically apply changes to all the designs that use the same row, or you can turn it into a standard row, and edits will only affect this design.',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'bars',
            title: 'Synced Row',
            ...args
          }
          const results = await onHandleUserInput(SyncedRow, args, theme, modalAPI)

          if (results === undefined) {
            resolve(true)
            return
          }

          const { isSynced = false, guid = '' } = results

          // route to edit single row
          if (guid) {
            navigate(`/edit/${guid}`)
            window.location.reload()
            return
          }
          resolve(isSynced)
        }
      },
      manageForm: {
        label: 'Insert Form',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'bars',
            title: 'Manage Form',
            ...args
          }
          const formFields = await onHandleUserInput(ManageForm, args, theme, modalAPI)
          tempStore.metadata = formFields
          resolve(formFields)
        }
      },
      mergeTags: {
        label: 'Search Tags',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'search',
            title: 'Search Merge Tags'
          }
          const mergetags = await onHandleUserInput(MergeTags, args, theme, modalAPI)
          resolve(mergetags)
        }
      },
      rowDisplayConditions: {
        label: 'Build',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'edit',
            title: 'Display Condition Builder'
          }
          const displayConditions = await onHandleUserInput(DisplayConditions, args, theme, modalAPI)
          resolve(displayConditions)
        }
      },
      externalContentURLs: {
        label: 'Find more rows...',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'bars',
            title: 'Select a category'
          }
          const search = await onHandleUserInput(Search, args, theme, modalAPI)
          resolve(search)
        }
      },
      getMention: {
        label: 'Find more users...',
        handler: async (resolve, reject, args) => {
          args.modal = {
            icon: 'bars',
            title: 'Invite a collaborator'
          }
          const mention = await onHandleUserInput(Mention, args, theme, modalAPI)
          resolve(mention)
        }
      },
    },
  })
}

export const hooksConfig = () => {
  return ({
    hooks: {
      getMentions: {
        label: 'Find more rows...',
        handler: async (resolve, reject, args) => {
          const mentionedPeople = [{
            'userColor': '#FF5733',
            'username': 'John Doe',
            'value': 'John Doe',
            'uid': '11c8482f-c05d-4480-9861-6d5c64ffbc4a'
          }, {
            'userColor': '#74FF33',
            'username': 'Jane Doe',
            'value': 'Jane Doe',
            'uid': 'ed43043f-1c15-4133-bedb-e78efb69e0ba'
          }, {
            'userColor': '#33A5FF',
            'username': 'Mario Rossi',
            'value': 'Riccardo Leschiutta',
            'uid': '45b9cdd1-60af-4235-ac9f-ddcb7b07a9e0'
          },
          {
            'userColor': '#6B33FF',
            'username': 'Cleo M. Fleetwood',
            'value': 'Cleo M. Fleetwood',
            'uid': '8e65c8f1-e222-4e1f-b7a5-c276968854ba'
          },
          {
            'userColor': '#FF3380',
            'username': 'Jamie J. Vanwormer',
            'value': 'Jamie J. Vanwormer',
            'uid': '04b3a1a3-ab04-46f7-b03b-e651357b54ab'
          }]

          let filtered = mentionedPeople
          if (typeof args === 'string') {
            filtered = mentionedPeople.filter(item => item.username.toLowerCase().includes(args.toLowerCase()))
          }
          resolve(filtered)
        }
      },
      brandTone: {
        handler: async (resolve, reject, { action, data }) => {
          switch (action) {
            case 'get':
              try {
                const actualBrandVoices = localStorage.brandVoices ? JSON.parse(localStorage.brandVoices) : []
                setTimeout(() => resolve(actualBrandVoices), 500)
              } catch (e) {
                reject()
              }
              break
            case 'save':
              try {
                const actualBrandVoices = localStorage.brandVoices ? JSON.parse(localStorage.brandVoices) : []
                const newBrandVoices = [...actualBrandVoices, data]
                localStorage.brandVoices = JSON.stringify(newBrandVoices)
                setTimeout(() => {
                  resolve()
                }, 1500)
              } catch (e) {
                reject()
              }
              break
            case 'saveSelected':
              try {
                localStorage.selectedBrandVoice = data
                resolve()
              } catch (e) {
                reject()
              }
              break
            case 'getSelected':
              resolve(localStorage.selectedBrandVoice)
              break
            case 'delete':
              try {
                const actualBrandVoices = localStorage.brandVoices ? JSON.parse(localStorage.brandVoices) : []
                const newBrandVoices = actualBrandVoices.filter(bv => bv.id !== data.id)
                localStorage.brandVoices = JSON.stringify(newBrandVoices)
                setTimeout(() => {
                  resolve()
                }, 1500)
              } catch (e) {
                reject()
              }
              break
            case 'edit':
              try {
                const actualBrandVoices = localStorage.brandVoices ? JSON.parse(localStorage.brandVoices) : []
                const brandVoiceToEdit = actualBrandVoices.findIndex(bv => bv.id === data.id)
                const newBrandVoices = actualBrandVoices.toSpliced(brandVoiceToEdit, 1, data)
                localStorage.brandVoices = JSON.stringify(newBrandVoices)
                setTimeout(() => {
                  resolve()
                }, 1500)
              } catch (e) {
                reject()
              }
              break
            default:
              reject(`Action ${action} not supported`)
          }
        }
      },
    },
  })
}

export const getRowJSON = page => {
  const pageJSON = JSON.parse(page)
  const rowJSON = pageJSON?.page?.rows?.[0] ?? {}
  return JSON.stringify(rowJSON)
}

export const actionsConfig = (dispatch, theme, eventAPI, modalAPI) => {
  const client = getClient()
  let totalTokens = 0

  return ({
    onInfo: function (data) {
      if (data.code === 1000) {
        const handle = data.detail.handle
        if (handle === 'ai-integration') {
          totalTokens = totalTokens + data.detail.usage.total_tokens
          // Refresh Usage AddOn Settings
          const refreshedUsageSettings = {
            addOns: [
              {
                id: 'ai-integration',
                settings: {
                  tokensAvailable: 1000,
                  tokensUsed: totalTokens,
                  tokenLabel: 'tokens',
                  isPromptDisabled: (totalTokens >= 1000) ? true : false,
                }
              },
            ],
          }

          // Reload Config
          console.log(refreshedUsageSettings)
          // bee.loadConfig(refreshedUsageSettings)
        }
      }
    },
    onError: function (a) {
      console.error('*** [integration] (onError) message a > ', a)
    },
    onChange: function (msg) {
      // console.log('ONCHANGE:  ', msg)
    },
    onWarning: function (a, b) {
      console.log('*** [integration] (onWarning) message a > ', a)
    },
    onSessionStarted: function (sessionInfo) {
      console.log('onSessionStarted:  ', sessionInfo)
      dispatch({
        payload: {
          sessionId: sessionInfo?.sessionId,
        }
      })
    },
    onSaveRow: (rowJSON, html, pageJSON) => {
      if (!rowJSON) return
      if (!pageJSON) return
      const row = JSON.parse(rowJSON)
      const page = JSON.parse(JSON.stringify(pageJSON))
      const meta = tempStore.metadata
      const request = {
        row: {
          ...row,
          meta
        },
        html,
        page,
        ...meta,
      }

      HTTP.post(`${client.demoAPI}/saveRow`, request)
        .then(({ data }) => {
          const { body } = data
          console.log('Row saved in Postgres: ', body)
        })
        .catch((error) => {
          console.log('Error: ', error)
        })
    },
    onSave: async (json, html) => {
      const { dequeue, events } = eventAPI
      const event = (events.length > 0) ? events[0] : undefined
      const mode = event.mode || 'row'
      dequeue()

      let title
      switch (mode) {
        case 'email':
          title = 'Save Email'
          break
        case 'page':
          title = 'Save Page'
          break
        case 'popup':
          title = 'Save Popup'
          break
        default:
          break
      }

      let icon
      switch (mode) {
        case 'email':
          icon = 'mail'
          break
        case 'page':
          icon = 'html5'
          break
        case 'popup':
          icon = 'html5'
          break
        default:
          break
      }

      if (event && event.name) {
        switch (event.name) {
          case 'saveMessage':
            const args = {
              modal: {
                icon,
                title,
              }
            }
            const id = event && event.id

            const data = !event?.isPrompt // if id, save over existing message no dialog
              ? { name: '' }
              : await onHandleUserInput(SaveMessage, args, theme, modalAPI)

            if (!data) return

            let endpoint
            switch (event.mode) {
              case 'email':
                endpoint = `${client.demoAPI}/saveMessage`
                break
              case 'page':
                endpoint = `${client.demoAPI}/savePage`
                break
              case 'popup':
                endpoint = `${client.demoAPI}/savePopup`
                break
              case 'row':
                endpoint = `${client.demoAPI}/saveRow`
                break
              default:
                endpoint = `${client.demoAPI}/saveRow`
                break
            }

            const payload = event.mode === 'row'
              ? {
                name: data.name,
                id,
                row: getRowJSON(json),
                page: json,
                html,
                merge: true,
                guid: event.guid,
              }
              : {
                name: data.name,
                id,
                json,
                html
              }

            HTTP.post(endpoint, payload)
              .then(({ data }) => {
                const { body } = data
                console.log('Message saved in db: ', body)
              })
              .catch((error) => {
                console.log('Error: ', error)
              })
            break
          case 'exportHTML':
            const template = Handlebars.compile(html)
            const processedHTML = template({ products })
            saveAs(
              new Blob([processedHTML], { type: 'text/plain;charset=utf-8' }),
              'template.html'
            )
            break
          case 'customPreview':
            //saveAs(new Blob([html], { type: 'text/plain;charset=utf-8' }), 'template.html');
            const modalStyles = {
              content: {
                width: '1000px',
                height: '75%',
              }
            }

            const template2 = Handlebars.compile(html)
            const processedHTML2 = template2({ products })

            const previewProps = {
              modal: {
                icon: 'file code outline',
                title: 'Custom Preview',
                modalStyles,
              },
              html: processedHTML2,
            }



            const results = await onHandleUserInput(CustomPreview, previewProps, theme, modalAPI)
            break
          default:
            saveAs(new Blob([html], { type: 'text/plain;charset=utf-8' }), 'template.html')
        }
      }
    },
    onPreview: function (html) {
      // Not implemented
    },
    onSend: function (html) {
      HTTP.post(`${client.demoAPI}/pdf`, {
        html
      })
        .then(({ data }) => {
          const { body } = data
          const { url } = body
          saveAs(url, 'template.pdf')
        })
        .catch((error) => {
          console.log('Error: ', error)
        })
    },
    addOns: [
      {
        id: 'ai-integration',
        settings: {
          isPromptDisabled: false,
          isSuggestionsDisabled: false,
          // tokensAvailable: 1000,
          // tokensUsed: 0,
          // tokenLabel: 'tokens',
          isBrandTonesEnabled: true,  // Whether Brand Tones feature is enabled
          canAddBrandTones: true, // Whether user can edit Brand Tones
          canSelectBrandTones: true,  // Whether user can select Brand Tones
          canEditBrandTones: true, // Whether user can edit Brand Tones
          canDeleteBrandTones: true, // Whether user can delete Brand Tones
        }
      },
    ],
  })
}
